import { defineStore } from 'pinia'
import axios from 'axios'
import Helpers from '../components/helper.js'
import { useAuthStore } from './authStore'
import { useNotificationsStore } from './toastNotifications'

export const useSolicitudMensajeriaStore = defineStore('solicitudMensajeria', {

  state: () => ({
    formData: {
      correoRemitente: "",
      empresaRemitente: "",
      tipoDestinatario: "",
      clienteDestinatario: "",
      empresaDestinataria: "",
      codigoPostal: "",
      tipoVialidad: "",
      nombreVialidad: "",
      numeroExterior: "",
      numeroInterior: "",
      colonia: "",
      alcaldiaMunicipio: "",
      entidad: "",
      entreCalle1: "",
      entreCalle2: "",
      personaRecibiraMensajeria:"",
      fechaEntrega: null,
      horarioMensajeria: "",
      tipoMensajeria: "",
      tituloCorreo: "",
      comentariosAdicionales: "",
      senderAddress: {
        codigoPostal: "",
        nombreVialidad: "",
        tipoVialidad: "",
        numeroExterior: "",
        numeroInterior: "",
        colonia: "",
        alcaldiaMunicipio: "",
        entidad: "",
        entreCalle1: "",
        entreCalle2: "",
      }
    },
    fetchingFlags: {
      oficinas: false,
      clientes: false,
      tiposDeMensajeria: false,
      empresas: false,
    },
    apiCache: {
      empresas: null,
      oficinas: [],
      clientes: [],
      tiposDeMensajeria: [],
    },
    errorMessages: {
      oficinas: null,
      clientes: null,
      tiposDeMensajeria: null,
      empresas: null,
    },
    currentStep: 1,
    stepsHeaders: [
      "Validación de Correo",
      "Selección de Remitente",
      "Dirección de Remitente",
      "Selección del Destinatario",
      "Dirección de Destinatario",
      "Información de Mensajeria",
      "Envío de Solicitud",
    ],
    defaultTiposDeMensajeria: ["Entrega de Documentos", "Recolección de Documentos", "Envío de Cheque", "Envío de Efectivo"]
  }),

  getters: {
    isLastStep: (state) => state.currentStep === state.stepsHeaders.length - 1,
    isFirstStep: (state) => state.currentStep === 1,
    requestFinished: (state) => state.currentStep === state.stepsHeaders.length,
    showClienteField: (state) => state.formData.tipoDestinatario === 'Clientes',
    showEmpresaField: (state) => state.formData.tipoDestinatario === 'Mensajeria Interna',
    showTituloCorreo: (state) =>
      state.formData.tipoMensajeria === 'Envío de Cheque' ||
      state.formData.tipoMensajeria === 'Envío de Efectivo'
  },

  actions: {

    trimString(str) {
      return str.length > 30 ? str.slice(0, 30) : str;
    },

    async fetchOficinas() {
      if (this.apiCache.oficinas.length) return this.apiCache.oficinas
      if (this.fetchingFlags.oficinas) return
      this.fetchingFlags.oficinas = true
      this.errorMessages.oficinas = null
      try {
        const authStore = useAuthStore();
        const response = await axios.get(
          `${process.env.VUE_APP_URL}/zoho/fetchInternalOffices`,
          { headers: { d_auth_token: `Bearer ${authStore.getToken}` } }
        )
        const recordsFound = response?.data?.recordMap;
        if (!recordsFound?.length) {
          throw new Error("No se encontraron oficinas");
        }

        this.apiCache.oficinas = recordsFound?.map(item => ({
          id: item.id,
          name: `${item.empresaName}${item.identificadorMensajeria ? ` - ${this.trimString(item.identificadorMensajeria)}` : ""}`,
          empresaId: item.empresaId,
          codigoPostal: item.codigoPostal?.toString() || "",
          nombreDeVialidad: item.nombreDeVialidad,
          tipoDeVialidad: item.tipoDeVialidad,
          numeroExterior: item.numeroExterior,
          numeroInterior: item.numeroInterior,
          colonia: item.colonia,
          alcaldiaMunicipio: item.alcaldiaMunicipio,
          entidad: item.entidad,
          entreCalle1: item.entreCalles1,
          entreCalle2: item.entreCalles2,
        })) || []
        return this.apiCache.oficinas
      } catch (error) {
        this.errorMessages.oficinas = error.message || "Error al obtener las oficinas destinatarias"
      } finally {
        this.fetchingFlags.oficinas = false
      }
    },

    async fetchClientes() {
      if (this.apiCache.clientes.length) return this.apiCache.clientes
      if (this.fetchingFlags.clientes) return
      this.fetchingFlags.clientes = true
      this.errorMessages.clientes = null
      try {
        const authStore = useAuthStore();
        const response = await axios.get(
          `${process.env.VUE_APP_URL}/zoho/fetchClients`,
          { headers: { d_auth_token: `Bearer ${authStore.getToken}` } }
        )
        const recordsFound = response?.data?.recordMap;
        if (!recordsFound?.length) {
          throw new Error("No se encontraron clientes");
        }

        this.apiCache.clientes = recordsFound?.map(item => ({
          id: item.id,
          name: item.Full_Name,
          codigoPostal: item.codigoPostal?.toString() || "",
          nombreDeVialidad: item.nombreDeVialidad,
          tipoDeVialidad: item.tipoDeVialidad,
          numeroExterior: item.numeroExterior,
          numeroInterior: item.numeroInterior,
          colonia: item.colonia,
          alcaldiaMunicipio: item.alcaldiaMunicipio,
          entidad: item.entidad,
          entreCalle1: item.entreCalle1,
          entreCalle2: item.entreCalle2,
        })) || []
        return this.apiCache.clientes
      } catch (error) {
        this.errorMessages.clientes = error.message || "Error al obtener los clientes"
      } finally {
        this.fetchingFlags.clientes = false
      }
    },

    async fetchTiposDeMensajeria() {
      try {
        if (this.apiCache.tiposDeMensajeria.length) return this.apiCache.tiposDeMensajeria
        if (this.fetchingFlags.tiposDeMensajeria) return
        this.fetchingFlags.tiposDeMensajeria = true
        const authStore = useAuthStore();
        const response = await axios.get(
          `${process.env.VUE_APP_URL}/zoho/fetchMessagingTypes`,
          { headers: { d_auth_token: `Bearer ${authStore.getToken}` } }
        )
        const picklistValues = response.data?.picklistValues;
        if (!picklistValues?.length) {
          throw new Error("No se encontraron tipos de mensajería")
        }
        let tempTiposDeMensajeria = [];
        for (const item of picklistValues) {
          tempTiposDeMensajeria.push(item);
        }
        this.tiposDeMensajeria = tempTiposDeMensajeria
        return this.tiposDeMensajeria
      } catch (error) {
        useNotificationsStore().showInfo("Error al obtener los tipos de mensajería, se utilizaran los tipos por defecto", 2500)
        return this.defaultTiposDeMensajeria
      } finally {
        this.fetchingFlags.tiposDeMensajeria = false
      }
    },

    async handleEmailValidation() {
      try {
        const verifyEmail = await axios.post(
          `${process.env.VUE_APP_URL}/zoho/verifyEmail`,
          {
            email: this.formData.correoRemitente,
            form: "mensajeria",
          }
        )
        const accessToken = verifyEmail?.data?.accessToken;
        if (!accessToken) {
          throw new Error("No se pudo obtener el token de acceso.");
        }
        const authStore = useAuthStore();
        authStore.setToken(accessToken);
        authStore.startTimer();
      } catch (error) {
        if (error.response?.status === 404 || error.response?.data?.message === "user not found") {
          throw new Error("El correo no se encuentra registrado en Usuarios de Mensajería o está inactivo.");
        }
        if (error.response?.status >= 500) {
          throw new Error("Error de conexión con el servidor. Por favor intente más tarde.");
        }
        if (error.response?.data?.errors?.length > 0) {
          throw new Error("Por favor proporcione un correo electrónico válido.");
        }
        throw new Error(error.response?.data?.message || error.message || "Correo electrónico inválido");
      }
    },

    handleSenderAddress() {
      const selectedEntity = this.apiCache.oficinas.find(item => item.id === this.formData.empresaRemitente)

      if (!selectedEntity) return

      Object.assign(this.formData.senderAddress, {
        codigoPostal: selectedEntity.codigoPostal?.toString() || "",
        nombreVialidad: selectedEntity.nombreDeVialidad,
        tipoVialidad: selectedEntity.tipoDeVialidad,
        numeroExterior: selectedEntity.numeroExterior,
        numeroInterior: selectedEntity.numeroInterior,
        colonia: selectedEntity.colonia,
        alcaldiaMunicipio: selectedEntity.alcaldiaMunicipio,
        entidad: selectedEntity.entidad,
        entreCalle1: selectedEntity.entreCalle1,
        entreCalle2: selectedEntity.entreCalle2,
      })
    },

    handleRecipientSelection() {
      const selectedEntity = this.formData.tipoDestinatario === "Mensajeria Interna"
        ? this.apiCache.oficinas.find(item => item.id === this.formData.empresaDestinataria)
        : this.apiCache.clientes.find(item => item.id === this.formData.clienteDestinatario)

      if (!selectedEntity) return

      Object.assign(this.formData, {
        codigoPostal: selectedEntity.codigoPostal?.toString() || "",
        nombreVialidad: selectedEntity.nombreDeVialidad,
        tipoVialidad: selectedEntity.tipoDeVialidad,
        numeroExterior: selectedEntity.numeroExterior,
        numeroInterior: selectedEntity.numeroInterior,
        colonia: selectedEntity.colonia,
        alcaldiaMunicipio: selectedEntity.alcaldiaMunicipio,
        entidad: selectedEntity.entidad,
        entreCalle1: selectedEntity.entreCalle1,
        entreCalle2: selectedEntity.entreCalle2,
      })
    },

    async handleFormSubmission() {
      try {
        const authStore = useAuthStore();
        const empresaRemitente = this.apiCache.oficinas.find(item => item.id === this.formData.empresaRemitente)?.empresaId;
        const empresaDestinatario = this.apiCache.oficinas.find(item => item.id === this.formData.empresaDestinataria)?.empresaId;
        const dataMap = {
          status: "Pendiente",
          Email: this.formData.correoRemitente,
          empresaRemitente: empresaRemitente || "",
          tipoDeDestinatario: this.formData.tipoDestinatario,
          clienteDestinatario: this.formData.tipoDestinatario === "Clientes" ? this.formData.clienteDestinatario : "",
          empresaDestinatario: this.formData.tipoDestinatario === "Mensajeria Interna" ? (empresaDestinatario || "") : "",
          codigoPostal: this.formData.codigoPostal,
          tipoDeVialidad: this.formData.tipoVialidad,
          nombreDeVialidad: this.formData.nombreVialidad,
          numeroExterior: this.formData.numeroExterior,
          numeroInterior: this.formData.numeroInterior,
          colonia: this.formData.colonia,
          alcaldiaMunicipio: this.formData.alcaldiaMunicipio,
          entidad: this.formData.entidad,
          entreCalle1: this.formData.entreCalle1,
          entreCalle2: this.formData.entreCalle2,
          codigoPostalEnvio: this.formData.senderAddress.codigoPostal,
          tipoDeVialidadEnvio: this.formData.senderAddress.tipoVialidad,
          nombreDeVialidadEnvio: this.formData.senderAddress.nombreVialidad,
          numeroExteriorEnvio: this.formData.senderAddress.numeroExterior,
          numeroInteriorEnvio: this.formData.senderAddress.numeroInterior,
          coloniaEnvio: this.formData.senderAddress.colonia,
          alcaldiaMunicipioEnvio: this.formData.senderAddress.alcaldiaMunicipio,
          entidadEnvio: this.formData.senderAddress.entidad,
          entreCalle1Envio: this.formData.senderAddress.entreCalle1,
          entreCalle2Envio: this.formData.senderAddress.entreCalle2,
          personaRecibiraMensajeria: this.formData.personaRecibiraMensajeria,
          fechaDeEntrega: this.formData.fechaEntrega ? this.formData.fechaEntrega.toISOString().split('T')[0] : "",
          horarioDeMensajeria: this.formData.horarioMensajeria,
          tipoDeMensajeria: this.formData.tipoMensajeria,
          tituloDeCorreo: (this.formData.tipoMensajeria === "Envío de Cheque" || this.formData.tipoMensajeria === "Envío de Efectivo") ? this.formData.tituloCorreo : "",
          comentariosAdicionales: this.formData.comentariosAdicionales,
        }

        const createRecord = await axios.post(
          `${process.env.VUE_APP_URL}/zoho/insertRecord`, {
            data: {
              module: "Solicitudes_de_Mensajeria",
              recordData: {
                data: [dataMap],
                trigger: ["workflow"]
              }
            }
        }, { headers: { d_auth_token: `Bearer ${authStore.getToken}` } }
        )
        if (!createRecord?.data?.details?.id) {
          throw new Error("Error al crear el registro")
        }

        Helpers.updateStateObject({
          objectName: "emptySpaceData",
          active: true,
          icon: "$success",
          title: "Solicitud Registrada",
          description: "Se te enviara un correo de confirmación, y se notificará a las personas correspondientes para su aprobación",
        })
      } catch (error) {
        console.log(error);
        throw new Error("Error al crear el registro")
      }
    },

    nextStep() {
      this.currentStep++
    },

    prevStep() {
      this.currentStep--
    }
  }
}) 